import { Card, CardContent, CardHeader, CardTitle } from './Card';
import { TrendingUp, TrendingDown, Users, Building2, Target, Award } from 'lucide-react';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

function KPICard({ title, value, change, icon, color }: KPICardProps) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {change >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                <span>{Math.abs(change)}% from last month</span>
              </div>
            )}
          </div>
          <div className={`p-4 rounded-xl ${color}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ChartData {
  label: string;
  value: number;
}

interface SimpleBarChartProps {
  data: ChartData[];
  color?: string;
  height?: number;
}

function SimpleBarChart({ data, color = '#3b82f6', height = 200 }: SimpleBarChartProps) {
  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-2">
        {data.map((item, index) => {
          const barHeight = (item.value / maxValue) * 100;
          return (
            <div key={index} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full bg-slate-100 rounded-t-lg relative" style={{ height: '100%' }}>
                <div
                  className="absolute bottom-0 w-full rounded-t-lg transition-all duration-500"
                  style={{
                    height: `${barHeight}%`,
                    backgroundColor: color
                  }}
                />
              </div>
              <span className="text-xs text-slate-500 truncate w-full text-center">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SimpleLineChartProps {
  data: ChartData[];
  color?: string;
  height?: number;
}

function SimpleLineChart({ data, color = '#3b82f6', height = 200 }: SimpleLineChartProps) {
  const maxValue = Math.max(...data.map(d => d.value));
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((item.value / maxValue) * 80 + 10);
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `0,100 ${points} 100,100`;

  return (
    <div className="w-full" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill={`url(#gradient-${color})`} />
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {data.map((item, index) => {
          const x = (index / (data.length - 1)) * 100;
          const y = 100 - ((item.value / maxValue) * 80 + 10);
          return (
            <circle key={index} cx={x} cy={y} r="2" fill={color} />
          );
        })}
      </svg>
      <div className="flex justify-between mt-2 text-xs text-slate-500">
        {data.map((item, index) => (
          <span key={index}>{item.label}</span>
        ))}
      </div>
    </div>
  );
}

interface SimpleDonutChartProps {
  data: Array<{ label: string; value: number; color: string }>;
}

function SimpleDonutChart({ data }: SimpleDonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;

  const slices = data.map(item => {
    const percentage = (item.value / total) * 100;
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle += angle;

    const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
    const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
    const x2 = 50 + 40 * Math.cos((endAngle - 90) * Math.PI / 180);
    const y2 = 50 + 40 * Math.sin((endAngle - 90) * Math.PI / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;

    return {
      ...item,
      percentage,
      pathData: `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
    };
  });

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="w-32 h-32">
        {slices.map((slice, index) => (
          <path
            key={index}
            d={slice.pathData}
            fill={slice.color}
            className="hover:opacity-80 transition-opacity"
          />
        ))}
        <circle cx="50" cy="50" r="25" fill="white" />
      </svg>
      <div className="flex-1 space-y-2">
        {slices.map((slice, index) => (
          <div key={index} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: slice.color }}
              />
              <span className="text-slate-600">{slice.label}</span>
            </div>
            <span className="font-medium text-slate-900">{slice.value} ({Math.round(slice.percentage)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface SalesKPIDashboardProps {
  stats: {
    totalCards: number;
    completedCards: number;
    syncedCards: number;
    uniqueCompanies: number;
    recentUploads: Array<{ id: string; firstName?: string; lastName?: string; company?: string; createdAt: Date }>;
  } | null;
}

export function SalesKPIDashboard({ stats }: SalesKPIDashboardProps) {
  // Sample trend data (in real app, this would come from API)
  const scanTrendData: ChartData[] = [
    { label: 'Jan', value: 45 },
    { label: 'Feb', value: 52 },
    { label: 'Mar', value: 38 },
    { label: 'Apr', value: 65 },
    { label: 'May', value: 78 },
    { label: 'Jun', value: 92 },
  ];

  const topScanners: ChartData[] = [
    { label: 'John', value: 45 },
    { label: 'Sarah', value: 38 },
    { label: 'Mike', value: 32 },
    { label: 'Emma', value: 28 },
    { label: 'Alex', value: 21 },
  ];

  const industryData = [
    { label: 'Technology', value: stats?.uniqueCompanies || 12, color: '#3b82f6' },
    { label: 'Finance', value: 8, color: '#8b5cf6' },
    { label: 'Healthcare', value: 6, color: '#ec4899' },
    { label: 'Retail', value: 5, color: '#f59e0b' },
    { label: 'Other', value: 4, color: '#64748b' },
  ];

  const conversionData = [
    { label: 'Scanned', value: stats?.totalCards || 100, color: '#3b82f6' },
    { label: 'Completed', value: stats?.completedCards || 75, color: '#10b981' },
    { label: 'Synced', value: stats?.syncedCards || 50, color: '#8b5cf6' },
    { label: 'Leads', value: 25, color: '#f59e0b' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Cards Scanned"
          value={stats?.totalCards || 0}
          change={12}
          icon={<Users className="w-6 h-6 text-blue-600" />}
          color="bg-blue-100"
        />
        <KPICard
          title="Conversion Rate"
          value={`${stats?.completedCards && stats?.totalCards ? Math.round((stats.completedCards / stats.totalCards) * 100) : 0}%`}
          change={8}
          icon={<Target className="w-6 h-6 text-emerald-600" />}
          color="bg-emerald-100"
        />
        <KPICard
          title="Companies Acquired"
          value={stats?.uniqueCompanies || 0}
          change={5}
          icon={<Building2 className="w-6 h-6 text-purple-600" />}
          color="bg-purple-100"
        />
        <KPICard
          title="CRM Sync Rate"
          value={`${stats?.syncedCards && stats?.totalCards ? Math.round((stats.syncedCards / stats.totalCards) * 100) : 0}%`}
          change={-3}
          icon={<Award className="w-6 h-6 text-amber-600" />}
          color="bg-amber-100"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scan Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Cards Scanned Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleLineChart data={scanTrendData} color="#3b82f6" />
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleDonutChart data={conversionData} />
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Scanners */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Performers</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleBarChart data={topScanners} color="#8b5cf6" />
          </CardContent>
        </Card>

        {/* Industry Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Companies by Industry</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleDonutChart data={industryData} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
